import { useContext, useRef, useState } from "react";
import { Badge, Button } from "flowbite-react";
import { ResponseContext } from "../../../context/responseProvider";
import { submitMerchRequest } from "../../../services/merchManager/merchManagerActions";
import { useMerchForm } from "../../../hooks/useMerchForm";
import { CustomLabel } from "../../../layouts/customLabel";
import { FormInputClass } from "../../../layouts/FormInputClass";
import { Dialog } from "@headlessui/react";
import { ImagePlaceholder } from "../../../layouts/icons";
import { StockBadge } from "../../../helpers/merch-manager/helper";
import { StatusContext } from "../../../context/statusProvider";

const DEFAULT_FORM = {
  item_count: "",
  item_purpose: "",
};

const REQUIRED = ["item_count", "item_purpose"];

const RequestMerchItem = ({ item, modalRef }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { getResponse, setResponse } = useContext(ResponseContext);
  const formRef = useRef(null);
  const { handleApi } = useContext(StatusContext);
  const response = getResponse("submit_merch_request");

  const { form, fieldErrors, disableBtn, handleChange, reset } = useMerchForm(
    null,
    DEFAULT_FORM,
    REQUIRED,
  );

  const handleClose = () => modalRef.current();

  const handleSubmit = (e) => {
    e.preventDefault();
    submitMerchRequest({
      form: {
        ...form,
        id: item.id,
        item_name: item.item_name,
      },
      email: localStorage.getItem("userEmail"),
      requesterName: localStorage.getItem("userName"),
      setIsLoading,
      setResponse,
      formRef,
      onSuccess: () => {
        reset();
        handleApi();
        handleClose();
      },
    });
  };

  return (
    <div className="w-full max-h-[80vh] overflow-y-auto">
      <Dialog.Title
        as="h3"
        className="text-lg font-medium leading-6 text-gray-900 inline-flex"
      >
        <strong className="flex align-middle">Request Item</strong>
      </Dialog.Title>

      <div className="flex h-full flex-col gap-4 p-6 pl-2">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex flex-col gap-6"
        >
          {/* ── Item preview ── */}
          <div className="flex gap-3 items-center rounded-xl bg-gray-50 dark:bg-gray-800 p-3">
            {item.assets?.[0] ? (
              <img
                src={item.assets[0].url}
                alt={item.item_name}
                className="w-16 h-16 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
                <ImagePlaceholder />
              </div>
            )}
            <div className="flex flex-col justify-center min-w-0 gap-1">
              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                {item.item_name}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {item.item_count} available
                </span>
                <Badge color={StockBadge(item.item_count).color} size="xs">
                  {StockBadge(item.item_count).label}
                </Badge>
              </div>
              {item.item_purpose && (
                <p className="text-xs text-gray-400">{item.item_purpose}</p>
              )}
            </div>
          </div>

          {/* ── Request details ── */}
          <div className="flex flex-col gap-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 pb-2 border-b border-gray-100 dark:border-gray-700">
              Request details
            </p>

            <div className="grid grid-cols-2 gap-4">
              {/* Count */}
              <div>
                <CustomLabel text="Count" required />
                <input
                  name="item_count"
                  type="number"
                  min="0"
                  max={item.item_count}
                  className={FormInputClass(fieldErrors.item_count)}
                  placeholder={`Max ${item.item_count}`}
                  value={form.item_count}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                />
                {fieldErrors.item_count && (
                  <p className="text-xs text-red-400 mt-1">
                    {fieldErrors.item_count}
                  </p>
                )}
              </div>
            </div>

            {/* Purpose */}
            <div>
              <CustomLabel text="Description/Purpose" required />
              <textarea
                name="item_purpose"
                rows={2}
                className={
                  FormInputClass(fieldErrors.item_purpose) + " resize-none"
                }
                placeholder="e.g. Client gifting, trade events"
                value={form.item_purpose}
                onChange={handleChange}
              />
              {fieldErrors.item_purpose && (
                <p className="text-xs text-red-400 mt-1">
                  {fieldErrors.item_purpose}
                </p>
              )}
            </div>
          </div>

          {/* ── API error ── */}
          {response?.status === false && (
            <Badge color="failure">{response?.message}</Badge>
          )}

          {/* ── Actions ── */}
          <div className="flex mt-2 items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              className="text-sm font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              onClick={handleClose}
            >
              Cancel
            </button>
            <Button type="submit" disabled={disableBtn || isLoading}>
              {isLoading ? "Submitting..." : "Request item"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestMerchItem;
